
import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Play, Pause, RotateCcw, Trophy, Clock } from 'lucide-react';
import { Game, GameSession, P5GameProps } from '@/types/games';
import { motion } from 'framer-motion';
import { DynamicGameRenderer } from './dynamic/DynamicGameRenderer';
import type { GameRunResult } from '@/types/gameSpec';

interface P5GameContainerProps {
  game: Game;
  gameSession: GameSession;
  onComplete: (session: GameSession) => void;
  onExit: () => void;
}

const P5GameContainer = ({ game, gameSession, onComplete, onExit }: P5GameContainerProps) => {
  // Short-circuit: AI-generated dynamic games use the universal renderer
  // and never touch the p5/canvas pipeline below.
  if (game.game_config?.gameType === 'ai_dynamic') {
    const handleDynamicComplete = (result: GameRunResult) => {
      onComplete({
        ...gameSession,
        score: result.score,
        max_score: result.max_score,
        time_spent: Math.round(result.time_spent_ms / 1000),
        problems_attempted: result.scene_count,
        problems_correct: result.scenes_correct,
        completed: true,
        session_data: {
          won: result.won,
          streak_max: result.streak_max,
          attempts: result.attempts,
          spec_version: result.spec_version,
        },
      });
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          <Card className="border-2 border-blue-200 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-2xl font-bold text-blue-700">
                    {game.name}
                  </CardTitle>
                  <p className="text-gray-600">{game.description}</p>
                </div>
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  Grade {gameSession.grade}
                </Badge>
              </div>
            </CardHeader>
          </Card>
          <DynamicGameRenderer
            spec={game.game_config}
            gameId={game.id}
            grade={gameSession.grade}
            onComplete={handleDynamicComplete}
            onExit={onExit}
          />
        </div>
      </div>
    );
  }

  const canvasRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [score, setScore] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [gameInstance, setGameInstance] = useState<any>(null);
  const [gameError, setGameError] = useState<string | null>(null);

  console.log('P5GameContainer: Rendered with game:', {
    name: game.name,
    gameType: game.game_config?.gameType,
    config: game.game_config,
    session: gameSession
  });

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying) {
      interval = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  const handleStart = () => {
    console.log('P5GameContainer: Starting game:', game.name);
    console.log('P5GameContainer: Game config:', game.game_config);
    console.log('P5GameContainer: Canvas ref available:', !!canvasRef.current);
    
    setIsPlaying(true);
    setGameError(null);
    initializeGame();
  };

  const handlePause = () => {
    console.log('P5GameContainer: Pausing game');
    setIsPlaying(false);
    if (gameInstance?.pause) {
      gameInstance.pause();
    }
  };

  const handleResume = () => {
    console.log('P5GameContainer: Resuming game');
    setIsPlaying(true);
    if (gameInstance?.resume) {
      gameInstance.resume();
    }
  };

  const handleReset = () => {
    console.log('P5GameContainer: Resetting game');
    setIsPlaying(false);
    setProgress(0);
    setScore(0);
    setTimeElapsed(0);
    setGameError(null);
    if (gameInstance?.reset) {
      gameInstance.reset();
    }
  };

  const initializeGame = async () => {
    if (!canvasRef.current) {
      console.error('P5GameContainer: Canvas ref not available');
      setGameError('Canvas not available');
      return;
    }

    console.log('P5GameContainer: Initializing game with canvas ref');

    const gameProps: P5GameProps = {
      game,
      gameSession,
      onProgressUpdate: (newProgress, newScore) => {
        console.log('P5GameContainer: Progress update:', newProgress, 'Score:', newScore);
        setProgress(newProgress);
        setScore(newScore);
      },
      onComplete: (finalScore, timeSpent, analytics) => {
        console.log('P5GameContainer: Game completed with score:', finalScore);
        setIsPlaying(false);
        const updatedSession = {
          ...gameSession,
          score: finalScore,
          time_spent: timeSpent,
          completed: true
        };
        onComplete(updatedSession);
      },
      onPause: handlePause,
      onResume: handleResume
    };

    // Get gameType from game config
    const gameType = game.game_config?.gameType;
    console.log('P5GameContainer: Detected gameType:', gameType);
    console.log('P5GameContainer: gameType type:', typeof gameType);
    console.log('P5GameContainer: gameType === "mathRunner":', gameType === 'mathRunner');
    
    if (!gameType) {
      console.error('P5GameContainer: No gameType found in game config');
      setGameError('Game type not specified in configuration');
      return;
    }

    try {
      console.log('P5GameContainer: Attempting to load game with type:', gameType);
      
      // Fixed switch statement with proper string matching
      switch (String(gameType).trim()) {
        case 'ai_dynamic':
          console.log('P5GameContainer: ai_dynamic — handled by inline renderer');
          // Marker only — actual rendering happens in JSX below via DynamicGameRenderer.
          // We don't instantiate a p5 sketch for dynamic games.
          setGameInstance({ kind: 'ai_dynamic' });
          break;

        case 'mathRunner':
          console.log('P5GameContainer: Loading Math Runner game...');
          try {
            const { MathRunnerGame } = await import('./math/MathRunnerGame');
            console.log('P5GameContainer: Math Runner module loaded successfully');
            
            const instance = new MathRunnerGame(canvasRef.current, gameProps);
            setGameInstance(instance);
            console.log('P5GameContainer: Math Runner game instance created successfully');
          } catch (importError) {
            console.error('P5GameContainer: Error importing/creating Math Runner:', importError);
            setGameError(`Failed to load Math Runner: ${importError.message}`);
          }
          break;
          
        case 'numberLine':
          console.log('P5GameContainer: Loading Number Line game...');
          try {
            const { NumberLineGame } = await import('./math/NumberLineGame');
            const instance = new NumberLineGame(canvasRef.current, gameProps);
            setGameInstance(instance);
            console.log('P5GameContainer: Number Line game instance created');
          } catch (importError) {
            console.error('P5GameContainer: Error loading NumberLine game:', importError);
            setGameError(`Failed to load Number Line game: ${importError.message}`);
          }
          break;
          
        case 'fractions':
          console.log('P5GameContainer: Loading Fraction game...');
          try {
            const { FractionGame } = await import('./math/FractionGame');
            const instance = new FractionGame(canvasRef.current, gameProps);
            setGameInstance(instance);
            console.log('P5GameContainer: Fraction game instance created');
          } catch (importError) {
            console.error('P5GameContainer: Error loading Fraction game:', importError);
            setGameError(`Failed to load Fraction game: ${importError.message}`);
          }
          break;
          
        case 'penguinMath':
          console.log('P5GameContainer: Loading Penguin Math game...');
          try {
            const { PenguinMathGame } = await import('./math/PenguinMathGame');
            const instance = new PenguinMathGame(canvasRef.current, gameProps);
            setGameInstance(instance);
            console.log('P5GameContainer: Penguin Math game instance created');
          } catch (importError) {
            console.error('P5GameContainer: Error loading Penguin Math game:', importError);
            setGameError(`Failed to load Penguin Math game: ${importError.message}`);
          }
          break;
          
        case 'phonics':
          console.log('P5GameContainer: Loading Phonics game...');
          try {
            const { PhonicsGame } = await import('./reading/PhonicsGame');
            const instance = new PhonicsGame(canvasRef.current, gameProps);
            setGameInstance(instance);
            console.log('P5GameContainer: Phonics game instance created');
          } catch (importError) {
            console.error('P5GameContainer: Error loading Phonics game:', importError);
            setGameError(`Failed to load Phonics game: ${importError.message}`);
          }
          break;
          
        default:
          console.warn('P5GameContainer: Unknown game type:', gameType);
          console.log('P5GameContainer: Available game types: ai_dynamic, mathRunner, numberLine, fractions, penguinMath, phonics');
          setGameError(`Unknown game type: "${gameType}". Available types: ai_dynamic, mathRunner, numberLine, fractions, penguinMath, phonics`);
      }
    } catch (error) {
      console.error('P5GameContainer: Error in game initialization:', error);
      setGameError(`Game initialization failed: ${error.message}`);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (level: number) => {
    switch (level) {
      case 1: return 'bg-green-100 text-green-700';
      case 2: return 'bg-yellow-100 text-yellow-700';
      case 3: return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex flex-col">
      <main className="flex-1 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          {/* Game Header */}
          <Card className="mb-6 border-2 border-blue-200 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-bold text-blue-700 mb-2">
                    {game.name}
                  </CardTitle>
                  <p className="text-gray-600">{game.description}</p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    Grade {gameSession.grade}
                  </Badge>
                  <Badge variant="secondary" className={getDifficultyColor(gameSession.difficulty_level || 1)}>
                    Level {gameSession.difficulty_level || 1}
                  </Badge>
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                    {game.game_type.charAt(0).toUpperCase() + game.game_type.slice(1)}
                  </Badge>
                </div>
              </div>
              
              {/* Progress and Stats */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm text-gray-600">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-3" />
                </div>
                
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  <span className="font-medium">Score: {score}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-500" />
                  <span className="font-medium">Time: {formatTime(timeElapsed)}</span>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Game Canvas Container */}
          <Card className="mb-6">
            <CardContent className="p-0">
              <div className="relative">
                {!isPlaying && (
                  <div className="absolute inset-0 bg-black/10 backdrop-blur-sm z-10 flex items-center justify-center">
                    <Button
                      onClick={handleStart}
                      size="lg"
                      className="bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-8 rounded-full text-xl shadow-lg"
                    >
                      <Play className="h-6 w-6 mr-2" />
                      Start Game
                    </Button>
                  </div>
                )}
                
                {gameError && (
                  <div className="absolute inset-0 bg-red-50 z-10 flex items-center justify-center">
                    <div className="text-center p-8">
                      <p className="text-red-600 mb-4">Game Error: {gameError}</p>
                      <Button onClick={() => setGameError(null)} variant="outline">
                        Try Again
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Game Canvas Container */}
                <div
                  ref={canvasRef}
                  className="w-full h-full bg-white rounded-lg"
                  style={{ minHeight: '600px', height: '600px' }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Game Controls */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {isPlaying ? (
                    <Button onClick={handlePause} variant="outline">
                      <Pause className="h-4 w-4 mr-2" />
                      Pause
                    </Button>
                  ) : progress > 0 ? (
                    <Button onClick={handleResume} variant="outline">
                      <Play className="h-4 w-4 mr-2" />
                      Resume
                    </Button>
                  ) : null}
                  
                  <Button onClick={handleReset} variant="outline">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset
                  </Button>
                </div>
                
                <div className="flex gap-2">
                  <Button onClick={onExit} variant="outline">
                    Exit Game
                  </Button>
                </div>
              </div>
              
              {/* Game Controls Info */}
              {game.game_config?.gameType === 'mathRunner' && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Game Controls:</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      Use <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">A</kbd> and <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">D</kbd> keys to move left and right
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      Press <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">W</kbd>, <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">Enter</kbd>, or <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">Space</kbd> to select answer
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      Or use number keys <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">1</kbd>, <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">2</kbd>, <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">3</kbd> for direct selection
                    </li>
                  </ul>
                </div>
              )}

              {game.game_config?.gameType === 'penguinMath' && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Game Controls:</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      Use <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">←</kbd> and <kbd className="px-1 py-0.5 bg-green-200 rounded text-xs">→</kbd> arrow keys to move penguin
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      Catch the correct answer by moving under falling number boxes
                    </li>
                  </ul>
                </div>
              )}

              {/* Learning Objectives */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Learning Objectives:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  {game.learning_objectives?.map((objective, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-2">•</span>
                      {objective}
                    </li>
                  )) || []}
                </ul>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
      
    </div>
  );
};

export default P5GameContainer;
